import { useEffect } from 'react';
import { useUnit } from 'effector-react';
import { Controller, useForm } from 'react-hook-form';
import { $countryProvincesStore, $countryStore, $currencyStore, $recipientByIdStore, $recipientUpdateStore } from '@lib/store';
import { useDebounce } from '@shared/hooks';
import { IbanUtils, ValidateUtils } from '@shared/utils';
import {
  type RecipientResponse,
  type RecipientUpdateRequest,
  TransferRecipientAccountTypeEnum,
  TransferRecipientTypeEnum,
} from '@shared/api/rest/recipients';
import { BaseInput, Button, ColorsEnum, ErrorMessage, Modal, Select, Text } from '@shared/widgets';

const OPTIONS_TYPE = Object.entries(TransferRecipientTypeEnum).map((x) => ({ label: x[1], value: x[1] }));
const OPTIONS_ACCOUNT_TYPE = Object.entries(TransferRecipientAccountTypeEnum).map((x) => ({ label: x[1], value: x[1] }));

interface IForm {
  type: TransferRecipientTypeEnum;
  firstName: string;
  lastName: string;
  idNumber: string;
  companyName: string;
  accountNumber: string;
  accountType: TransferRecipientAccountTypeEnum;
  iban: string;
  bankName: string;
  bankProvince: string;
  bankAddress: string;
  bankCity: string;
  branchCode: string;
  bankCountryCode: string;
  bicOrSwiftCode: string;
  countryCode: string;
  currency: string;
  email: string;
  address: string;
  postCode: string;
  city: string;
  province: string;
}

interface IModalEditRecipientProps {
  isOpen: boolean;
  onClose: () => void;
  id: string;
  data: RecipientResponse;
}

export const ModalEditRecipient = (props: IModalEditRecipientProps) => {
  const countryProvincesStore = useUnit($countryProvincesStore.store);
  const countriesStore = useUnit($countryStore.store);
  const currenciesStore = useUnit($currencyStore.store);

  const { control, formState, handleSubmit, watch, setValue, setError, clearErrors, reset } = useForm<IForm>({
    defaultValues: {
      type: props.data.type || '',
      firstName: props.data.firstName || '',
      lastName: props.data.lastName || '',
      idNumber: props.data.idNumber || '',
      companyName: props.data.companyName || '',
      accountNumber: props.data.accountNumber || '',
      accountType: props.data.accountType || '',
      iban: props.data.iban || '',
      bankName: props.data.bankName || '',
      bankProvince: props.data.bankProvince || '',
      bankAddress: props.data.bankAddress || '',
      bankCity: props.data.bankCity || '',
      branchCode: props.data.branchCode || '',
      bankCountryCode: props.data.bankCountryCode || '',
      bicOrSwiftCode: props.data.bic || '',
      countryCode: props.data.countryCode || '',
      currency: props.data.currency || '',
      email: props.data.email || '',
      address: props.data.address || '',
      postCode: props.data.postCode || '',
      city: props.data.city || '',
      province: props.data.province || '',
    },
  });
  const type = watch('type');
  const accountNumber = watch('accountNumber');
  const iban = watch('iban');
  const countryCode = watch('countryCode');

  useEffect(() => {
    $countryProvincesStore.events.update();
    $countryStore.events.update();
    $currencyStore.events.update();
  }, []);

  const debouncedIban = useDebounce(iban);
  useEffect(() => {
    if (!props.isOpen) return;
    (async () => {
      const ibanValidation = await IbanUtils.validateIban(debouncedIban);
      if (!ibanValidation.valid) setError('iban', { message: 'Provided incorrect IBAN number' });
      else {
        clearErrors(['iban', 'bankName', 'bankCountryCode', 'bicOrSwiftCode']);
        ibanValidation?.bankData?.name && setValue('bankName', ibanValidation.bankData.name);
        ibanValidation?.iban && setValue('bankCountryCode', ibanValidation.iban.slice(0, 2));
        ibanValidation?.bankData?.bic && setValue('bicOrSwiftCode', ibanValidation.bankData.bic);
      }
    })();
  }, [debouncedIban]);

  const closeHandler = () => {
    props.onClose();
    reset({
      type: props.data.type || '',
      firstName: props.data.firstName || '',
      lastName: props.data.lastName || '',
      idNumber: props.data.idNumber || '',
      companyName: props.data.companyName || '',
      accountNumber: props.data.accountNumber || '',
      accountType: props.data.accountType || '',
      iban: props.data.iban || '',
      bankName: props.data.bankName || '',
      bankProvince: props.data.bankProvince || '',
      bankAddress: props.data.bankAddress || '',
      bankCity: props.data.bankCity || '',
      branchCode: props.data.branchCode || '',
      bankCountryCode: props.data.bankCountryCode || '',
      bicOrSwiftCode: props.data.bic || '',
      countryCode: props.data.countryCode || '',
      currency: props.data.currency || '',
      email: props.data.email || '',
      address: props.data.address || '',
      postCode: props.data.postCode || '',
      city: props.data.city || '',
      province: props.data.province || '',
    });
  };
  const submitHandler = async (data: IForm) => {
    const { firstName, lastName, idNumber, companyName, ...rest } = data;

    let payload: RecipientUpdateRequest = {};
    if (data.type === TransferRecipientTypeEnum.Individual) payload = { ...rest, firstName, lastName, idNumber };
    if (data.type === TransferRecipientTypeEnum.Business) payload = { ...rest, companyName };

    await $recipientUpdateStore.events.update({
      ...payload,
      id: props.id,
      bankProvince: data.bankProvince || null,
      bankAddress: data.bankAddress || null,
      bankCity: data.bankCity || null,
      branchCode: data.branchCode || null,
      bankCountryCode: data.bankCountryCode || null,
      address: data.address || null,
      postCode: data.postCode || null,
      city: data.city || null,
      province: data.province || null,
    });
    props.onClose();
    $recipientByIdStore.events.getById(props.id);
  };

  const countryProvinces = countryCode in countryProvincesStore.data ? countryProvincesStore.data[countryCode] : [];
  const isIndividual = type === TransferRecipientTypeEnum.Individual;
  const isBusiness = type === TransferRecipientTypeEnum.Business;
  return (
    <Modal isOpen={props.isOpen} onClose={closeHandler} title=' Edit recipient details'>
      <form className='flex flex-col gap-4' onSubmit={handleSubmit(submitHandler)}>
        <Controller
          name='type'
          control={control}
          rules={{ required: ValidateUtils.requiredErrorMessage }}
          render={({ field, fieldState: { error } }) => (
            <div>
              <Text size='sm' color={ColorsEnum.Secondary}>
                Type
              </Text>
              <Select items={OPTIONS_TYPE} nullValue={{ isEnable: false, text: 'Type' }} value={field.value} onChange={field.onChange} />
              <ErrorMessage text={error?.message} />
            </div>
          )}
        />
        {isIndividual && (
          <Controller
            name='firstName'
            control={control}
            rules={{
              required: ValidateUtils.requiredErrorMessage,
              pattern: { value: ValidateUtils.formFieldRegex, message: ValidateUtils.formFieldErrorMessage },
            }}
            render={({ field, fieldState: { error } }) => (
              <div>
                <Text size='sm' color={ColorsEnum.Secondary}>
                  Name
                </Text>
                <BaseInput {...field} placeholder='Name' />
                <ErrorMessage text={error?.message} />
              </div>
            )}
          />
        )}
        {isIndividual && (
          <Controller
            name='lastName'
            control={control}
            rules={{
              required: ValidateUtils.requiredErrorMessage,
              pattern: { value: ValidateUtils.formFieldRegex, message: ValidateUtils.formFieldErrorMessage },
            }}
            render={({ field, fieldState: { error } }) => (
              <div>
                <Text size='sm' color={ColorsEnum.Secondary}>
                  Last Name
                </Text>
                <BaseInput {...field} placeholder='Last Name' />
                <ErrorMessage text={error?.message} />
              </div>
            )}
          />
        )}
        {isIndividual && (
          <Controller
            name='idNumber'
            control={control}
            rules={{ required: ValidateUtils.requiredErrorMessage }}
            render={({ field, fieldState: { error } }) => (
              <div>
                <Text size='sm' color={ColorsEnum.Secondary}>
                  Id Number
                </Text>
                <BaseInput {...field} placeholder='Id Number' />
                <ErrorMessage text={error?.message} />
              </div>
            )}
          />
        )}
        {isBusiness && (
          <Controller
            name='companyName'
            control={control}
            rules={{
              required: ValidateUtils.requiredErrorMessage,
              pattern: { value: ValidateUtils.formFieldRegex, message: ValidateUtils.formFieldErrorMessage },
            }}
            render={({ field, fieldState: { error } }) => (
              <div>
                <Text size='sm' color={ColorsEnum.Secondary}>
                  Company Name
                </Text>
                <BaseInput {...field} placeholder='Company Name' />
                <ErrorMessage text={error?.message} />
              </div>
            )}
          />
        )}
        <Controller
          name='accountNumber'
          control={control}
          rules={{
            required: !iban ? ValidateUtils.requiredErrorMessage : false,
            pattern: { value: ValidateUtils.formFieldRegex, message: ValidateUtils.formFieldErrorMessage },
          }}
          render={({ field, fieldState: { error } }) => (
            <div>
              <Text size='sm' color={ColorsEnum.Secondary}>
                Account Number
              </Text>
              <BaseInput {...field} placeholder='Account Number' />
              <ErrorMessage text={error?.message} />
            </div>
          )}
        />
        <Controller
          name='accountType'
          control={control}
          rules={{ required: ValidateUtils.requiredErrorMessage }}
          render={({ field, fieldState: { error } }) => (
            <div>
              <Text size='sm' color={ColorsEnum.Secondary}>
                Account type
              </Text>
              <Select
                items={OPTIONS_ACCOUNT_TYPE}
                nullValue={{ isEnable: false, text: 'Account type' }}
                value={field.value}
                onChange={field.onChange}
              />
              <ErrorMessage text={error?.message} />
            </div>
          )}
        />
        <Controller
          name='iban'
          control={control}
          rules={{ required: !accountNumber ? ValidateUtils.requiredErrorMessage : false }}
          render={({ field, fieldState: { error } }) => (
            <div>
              <Text size='sm' color={ColorsEnum.Secondary}>
                IBAN Number
              </Text>
              <BaseInput {...field} placeholder='IBAN Number' />
              <ErrorMessage text={error?.message} />
            </div>
          )}
        />
        <Controller
          name='bankName'
          control={control}
          rules={{
            required: ValidateUtils.requiredErrorMessage,
            pattern: { value: ValidateUtils.formFieldRegex, message: ValidateUtils.formFieldErrorMessage },
          }}
          render={({ field, fieldState: { error } }) => (
            <div>
              <Text size='sm' color={ColorsEnum.Secondary}>
                Bank Name
              </Text>
              <BaseInput {...field} placeholder='Bank Name' />
              <ErrorMessage text={error?.message} />
            </div>
          )}
        />
        <Controller
          name='bankProvince'
          control={control}
          rules={{ pattern: { value: ValidateUtils.formFieldRegex, message: ValidateUtils.formFieldErrorMessage } }}
          render={({ field, fieldState: { error } }) => (
            <div>
              <Text size='sm' color={ColorsEnum.Secondary}>
                Bank Province
              </Text>
              <Select
                items={countryProvinces.map((countryProvince) => ({ label: countryProvince, value: countryProvince }))}
                nullValue={{ isEnable: false, text: 'Bank Province / Region' }}
                value={field.value}
                onChange={field.onChange}
              />
              <ErrorMessage text={error?.message} />
            </div>
          )}
        />
        <Controller
          name='bankAddress'
          control={control}
          rules={{ pattern: { value: ValidateUtils.formFieldRegex, message: ValidateUtils.formFieldErrorMessage } }}
          render={({ field, fieldState: { error } }) => (
            <div>
              <Text size='sm' color={ColorsEnum.Secondary}>
                Bank Address
              </Text>
              <BaseInput {...field} placeholder='Bank Address' />
              <ErrorMessage text={error?.message} />
            </div>
          )}
        />
        <Controller
          name='bankCity'
          control={control}
          rules={{ pattern: { value: ValidateUtils.formFieldRegex, message: ValidateUtils.formFieldErrorMessage } }}
          render={({ field, fieldState: { error } }) => (
            <div>
              <Text size='sm' color={ColorsEnum.Secondary}>
                Bank City
              </Text>
              <BaseInput {...field} placeholder='Bank City' />
              <ErrorMessage text={error?.message} />
            </div>
          )}
        />
        <Controller
          name='branchCode'
          control={control}
          rules={{ pattern: { value: ValidateUtils.formFieldRegex, message: ValidateUtils.formFieldErrorMessage } }}
          render={({ field, fieldState: { error } }) => (
            <div>
              <Text size='sm' color={ColorsEnum.Secondary}>
                Branch Code
              </Text>
              <BaseInput {...field} placeholder='Branch Code' />
              <ErrorMessage text={error?.message} />
            </div>
          )}
        />
        <Controller
          name='bankCountryCode'
          control={control}
          render={({ field, fieldState: { error } }) => (
            <div>
              <Text size='sm' color={ColorsEnum.Secondary}>
                Bank Country
              </Text>
              <Select
                enableSearch
                items={countriesStore.data.map((country) => ({ label: country.name, value: country.twoLetterCode }))}
                nullValue={{ isEnable: false, text: 'Country' }}
                value={field.value}
                onChange={field.onChange}
              />
              <ErrorMessage text={error?.message} />
            </div>
          )}
        />
        <Controller
          name='bicOrSwiftCode'
          control={control}
          rules={{
            required: ValidateUtils.requiredErrorMessage,
            pattern: { value: ValidateUtils.formFieldRegex, message: ValidateUtils.formFieldErrorMessage },
            validate: (value) => {
              if (!ValidateUtils.validateBicCodeField(value)) return ValidateUtils.bicSwiftErrorMessage;
              return true;
            },
          }}
          render={({ field, fieldState: { error } }) => (
            <div>
              <Text size='sm' color={ColorsEnum.Secondary}>
                BIC/Swift code
              </Text>
              <BaseInput {...field} placeholder='BIC/Swift code' />
              <ErrorMessage text={error?.message} />
            </div>
          )}
        />
        <Controller
          name='countryCode'
          control={control}
          rules={{ required: ValidateUtils.requiredErrorMessage }}
          render={({ field, fieldState: { error } }) => (
            <div>
              <Text size='sm' color={ColorsEnum.Secondary}>
                Country
              </Text>
              <Select
                enableSearch
                items={countriesStore.data.map((country) => ({ label: country.name, value: country.twoLetterCode }))}
                nullValue={{ isEnable: false, text: 'Country' }}
                value={field.value}
                onChange={field.onChange}
              />
              <ErrorMessage text={error?.message} />
            </div>
          )}
        />
        <Controller
          name='currency'
          control={control}
          rules={{ required: ValidateUtils.requiredErrorMessage }}
          render={({ field, fieldState: { error } }) => (
            <div>
              <Text size='sm' color={ColorsEnum.Secondary}>
                Currency
              </Text>
              <Select
                items={currenciesStore.data.map((currency) => ({ label: currency, value: currency }))}
                nullValue={{ isEnable: false, text: 'Currency' }}
                value={field.value}
                onChange={field.onChange}
              />
              <ErrorMessage text={error?.message} />
            </div>
          )}
        />
        <Controller
          name='email'
          control={control}
          rules={{
            required: ValidateUtils.requiredErrorMessage,
            pattern: { value: ValidateUtils.emailRegex, message: ValidateUtils.emailErrorMessage },
          }}
          render={({ field, fieldState: { error } }) => (
            <div>
              <Text size='sm' color={ColorsEnum.Secondary}>
                Email
              </Text>
              <BaseInput {...field} placeholder='Email' />
              <ErrorMessage text={error?.message} />
            </div>
          )}
        />
        <Controller
          name='address'
          control={control}
          rules={{ pattern: { value: ValidateUtils.formFieldRegex, message: ValidateUtils.formFieldErrorMessage } }}
          render={({ field, fieldState: { error } }) => (
            <div>
              <Text size='sm' color={ColorsEnum.Secondary}>
                Address
              </Text>
              <BaseInput {...field} placeholder='Address' />
              <ErrorMessage text={error?.message} />
            </div>
          )}
        />
        <Controller
          name='postCode'
          control={control}
          rules={{ pattern: { value: ValidateUtils.formFieldRegex, message: ValidateUtils.formFieldErrorMessage } }}
          render={({ field, fieldState: { error } }) => (
            <div>
              <Text size='sm' color={ColorsEnum.Secondary}>
                Post Code
              </Text>
              <BaseInput {...field} placeholder='Post Code' />
              <ErrorMessage text={error?.message} />
            </div>
          )}
        />
        <Controller
          name='city'
          control={control}
          rules={{ pattern: { value: ValidateUtils.formFieldRegex, message: ValidateUtils.formFieldErrorMessage } }}
          render={({ field, fieldState: { error } }) => (
            <div>
              <Text size='sm' color={ColorsEnum.Secondary}>
                Town / City
              </Text>
              <BaseInput {...field} placeholder='Town / City' />
              <ErrorMessage text={error?.message} />
            </div>
          )}
        />
        <Controller
          name='province'
          control={control}
          rules={{ pattern: { value: ValidateUtils.formFieldRegex, message: ValidateUtils.formFieldErrorMessage } }}
          render={({ field, fieldState: { error } }) => (
            <div>
              <Text size='sm' color={ColorsEnum.Secondary}>
                Province / Region
              </Text>
              <BaseInput {...field} placeholder='Province / Region' />
              <ErrorMessage text={error?.message} />
            </div>
          )}
        />
        <Button className='rounded' fullWidth color='primary' type='submit' disabled={formState.isSubmitting}>
          Save
        </Button>
      </form>
    </Modal>
  );
};
